import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { apiGet } from '../../api/client.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatDistanceToNow } from 'date-fns';

/**
 * Admin / Quotas dashboard — ADR-0015 §5.
 *
 * Lists every org with current usage vs caps. Rows highlight when:
 *   - usage > 80% of cap (yellow)
 *   - usage > cap (red — quota currently rejecting requests)
 *
 * Also shows the last 7 days of quota_violations and the last 24h of
 * AI spend per org/model.
 */
export default function AdminQuotas() {
  const { user } = useAuth();
  // Platform-operator only (matches the server's requirePlatformAdmin gate —
  // an org-role check here would render, then 403 on every request).
  const isOperator = !!user?.is_platform_admin;

  // Hooks before the redirect guard (rules of hooks); `enabled` stops
  // non-operators from firing the request at all.
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-quotas'],
    queryFn: () => apiGet('/admin/quotas'),
    refetchInterval: 30_000,
    enabled: isOperator,
  });

  // Per-customer economics (ADR-0031): revenue vs AI COGS vs credits.
  // Filterable by window / org / project (owner ask 2026-07-25).
  const [econDays, setEconDays] = useState(30);
  const [econOrg, setEconOrg] = useState('');
  const [econProject, setEconProject] = useState('');
  const econQ = useQuery({
    queryKey: ['admin-ai-spend', econDays, econOrg, econProject],
    queryFn: () => apiGet(
      `/admin/ai-spend?days=${econDays}`
      + (econOrg ? `&org=${encodeURIComponent(econOrg)}` : '')
      + (econProject ? `&project=${encodeURIComponent(econProject)}` : '')
    ),
    refetchInterval: 60_000,
    enabled: isOperator,
  });
  // Option lists: orgs from the quotas payload; projects from the endpoint's
  // dedicated project_options (org-scoped, never narrowed by the project
  // filter itself — deriving them from filtered rows collapsed the dropdown
  // to just the selected project, the bug reported 2026-07-25).
  const projectOptions = econQ.data?.project_options || [];

  if (!isOperator) return <Navigate to="/dashboard" replace />;

  if (isLoading) return <div className="empty">Loading quotas…</div>;
  if (error)     return <div className="empty">Couldn't load: {error.message}</div>;
  if (!data)     return null;

  return (
    <div className="admin-quotas-page">
      <h2>Quotas dashboard</h2>
      <p className="meta">
        Per-org current usage vs limit. Auto-refreshes every 30s.{' '}
        <Link to="/admin">← Back to admin overview</Link>
      </p>

      <table className="admin-quotas-table">
        <thead>
          <tr>
            <th>Org</th>
            <th>Tier</th>
            <th>Status</th>
            <th>Posts</th>
            <th>Projects</th>
            <th>Social</th>
            <th>AI today</th>
            <th>Resets</th>
          </tr>
        </thead>
        <tbody>
          {data.organizations.map((o) => (
            <tr key={o.id} className={rowClass(o)}>
              <td>
                <strong>{o.name}</strong>
                <br /><small>{o.slug}</small>
              </td>
              <td><span className={`plan-pill plan-${o.plan_tier || 'trial'}`}>{o.plan_tier || 'trial'}</span></td>
              <td>{o.billing_status || o.org_status}</td>
              <td className="num">{o.posts_used_this_month ?? 0} / {o.posts_per_month ?? '—'}</td>
              <td className="num">{o.projects_used} / {o.projects_max ?? '—'}</td>
              <td className="num">{o.social_used} / {o.social_accounts_max ?? '—'}</td>
              <td className="num">{o.ai_calls_used_today ?? 0} / {o.ai_calls_per_day ?? '—'}</td>
              <td>{o.reset_at ? formatDistanceToNow(new Date(o.reset_at), { addSuffix: true }) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginTop: 32 }}>Customer economics (month to date)</h3>
      <p className="meta">
        What each customer pays vs what their AI use costs — the margin view for sizing caps and credit packs.
      </p>

      {/* Filters — apply to the economics + activity + 24h tables below.
          Note: 'Customer economics' rows are ORGANIZATIONS (billing entities);
          markaii + clilancers are PROJECTS under the dclacorp org — pick them
          in the Project filter to isolate a brand. */}
      <div className="admin-econ-filters">
        <label>
          Window{' '}
          <select value={econDays} onChange={(e) => setEconDays(Number(e.target.value))}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
        <label>
          Org{' '}
          <select value={econOrg} onChange={(e) => { setEconOrg(e.target.value); setEconProject(''); }}>
            <option value="">All orgs</option>
            {(data.organizations || []).map((o) => (
              <option key={o.slug} value={o.slug}>{o.slug}</option>
            ))}
          </select>
        </label>
        <label>
          Project{' '}
          <select value={econProject} onChange={(e) => setEconProject(e.target.value)}>
            <option value="">All projects</option>
            {projectOptions.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>
      <table className="admin-quotas-table">
        <thead>
          <tr>
            <th>Org</th><th>Tier</th><th>MRR</th><th>AI cost (MTD)</th>
            <th>Margin</th><th>Credits left (plan + topup)</th>
          </tr>
        </thead>
        <tbody>
          {(econQ.data?.org_economics || []).map((o) => (
            <tr key={o.slug}>
              <td><strong>{o.name}</strong><br /><small>{o.slug}</small></td>
              <td><span className={`plan-pill plan-${o.plan_tier || 'trial'}`}>{o.plan_tier || 'trial'}</span></td>
              <td className="num">${Number(o.mrr_usd || 0).toFixed(0)}</td>
              <td className="num">${Number(o.ai_cogs_mtd_usd || 0).toFixed(2)}</td>
              <td className="num">
                {o.margin_pct == null ? '—' : `$${Number(o.margin_usd).toFixed(0)} (${o.margin_pct}%)`}
              </td>
              <td className="num">
                {o.credits_unlimited
                  ? '∞ (internal)'
                  : `${Number(o.credits_plan_remaining ?? 0).toLocaleString()} + ${Number(o.credits_topup ?? 0).toLocaleString()}`}
              </td>
            </tr>
          ))}
          {econQ.isLoading && <tr><td colSpan="6">Loading…</td></tr>}
        </tbody>
      </table>

      <h3 style={{ marginTop: 32 }}>Spend by activity (last {econDays} days)</h3>
      <p className="meta">
        What the AI budget actually buys per brand: post content (text + images), automatic
        comment answers, and analysis overhead.
      </p>
      <table className="admin-quotas-table">
        <thead>
          <tr><th>Org</th><th>Project</th><th>Activity</th><th>Calls</th><th>Cost</th><th>≈ Credits</th></tr>
        </thead>
        <tbody>
          {(econQ.data?.by_project_activity || []).map((r, i) => (
            <tr key={i}>
              <td>{r.organization_slug}</td>
              <td>{r.project_key}</td>
              <td>{{
                content_images: '🖼️ Content — AI images',
                content_text: '✍️ Content — writing',
                auto_answers: '💬 Automatic answers',
                analysis: '📊 Analysis & planning',
                other: 'Other',
              }[r.activity] || r.activity}</td>
              <td className="num">{r.calls}</td>
              <td className="num">${Number(r.cost_usd || 0).toFixed(2)}</td>
              <td className="num">{Number(r.credits || 0).toLocaleString()}</td>
            </tr>
          ))}
          {econQ.isLoading && <tr><td colSpan="6">Loading…</td></tr>}
        </tbody>
      </table>

      <h3 style={{ marginTop: 32 }}>AI spend (last 24h)</h3>
      <table className="admin-quotas-table">
        <thead>
          <tr><th>Org</th><th>Model</th><th>Requests</th><th>Errors</th><th>Cost</th></tr>
        </thead>
        <tbody>
          {data.recent_ai_spend_24h.length === 0 ? (
            <tr><td colSpan={5} className="meta">No AI calls in the last 24h.</td></tr>
          ) : data.recent_ai_spend_24h.map((s, i) => (
            <tr key={i}>
              <td>{s.organization_slug}</td>
              <td>{s.model}</td>
              <td className="num">{s.request_count}</td>
              <td className="num">{s.error_count}</td>
              <td className="num">${Number(s.cost_usd || 0).toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginTop: 32 }}>Recent quota violations (last 7 days)</h3>
      <table className="admin-quotas-table">
        <thead>
          <tr><th>When</th><th>Org</th><th>Kind</th><th>Detail</th></tr>
        </thead>
        <tbody>
          {data.recent_violations.length === 0 ? (
            <tr><td colSpan={4} className="meta">No violations recorded.</td></tr>
          ) : data.recent_violations.map((v) => (
            <tr key={v.id}>
              <td>{formatDistanceToNow(new Date(v.created_at), { addSuffix: true })}</td>
              <td>{v.organization_slug || '—'}</td>
              <td>{v.kind}</td>
              <td><code>{JSON.stringify(v.detail || {})}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function rowClass(org) {
  const used = (a, b) => (b > 0 ? a / b : 0);
  const ratios = [
    used(org.posts_used_this_month, org.posts_per_month),
    used(org.projects_used, org.projects_max),
    used(org.social_used, org.social_accounts_max),
    used(org.ai_calls_used_today, org.ai_calls_per_day),
  ];
  if (ratios.some((r) => r >= 1))  return 'over';
  if (ratios.some((r) => r >= 0.8)) return 'warn';
  return '';
}
